import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { recruitmentCompanyService } from '../../services/recruitmentCompanyService';
import { authService } from '../../services/authService';
import RecruitmentCompanyNav from '../../components/RecruitmentCompanyNav';
import RecruitmentCompanyBottomNavigation from '../../components/RecruitmentCompanyBottomNavigation';
import Loader from '../../components/Loader';
import { useNavigate, useParams } from 'react-router-dom';

const UserEdit = () => {
  const user = authService.getCurrentUser();
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [role, setRole] = useState('employee');
  const navigate = useNavigate();

  const load = async () => {
    try {
      setLoading(true);
      const res = await recruitmentCompanyService.getUsers();
      const list = Array.isArray(res) ? res : (res?.data ?? []);
      const found = (list || []).find(u => String(u.id) === String(userId));
      if (!found) {
        toast.error('المستخدم غير موجود');
        navigate('/recruitment-company/users');
        return;
      }
      setTargetUser(found);
      setRole(found?.pivot?.role || 'employee');
    } catch (error) {
      const message = error?.response?.data?.message || 'فشل تحميل بيانات المستخدم';
      toast.error(message);
      navigate('/recruitment-company/users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await recruitmentCompanyService.updateUser(userId, { role });
      toast.success('تم تحديث بيانات المستخدم');
      navigate('/recruitment-company/users');
    } catch (error) {
      const message = error?.response?.data?.message || 'فشل تحديث البيانات';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loader fullScreen={true} size="large" />;
  }

  return (
    <div className="employee-home-page" dir="rtl">
      <RecruitmentCompanyNav user={user} />

      {/* Header */}
      <div className="px-3 mb-3 mt-3">
        <div className="edit-hero">
          <div className="edit-hero-inner">
            <button
              className="settings-back-btn"
              onClick={() => navigate(-1)}
              style={{ background: 'transparent', border: 0 }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
            <div className="edit-hero-badge">✏️</div>
            <div className="edit-hero-text flex-grow-1">
              <div className="edit-hero-title">تعديل مستخدم</div>
              <div className="edit-hero-subtitle">{targetUser?.name} — {targetUser?.email}</div>
            </div>
            <div className="user-cell">
              <div className="avatar-initial">
                {(targetUser?.name || 'U').charAt(0).toUpperCase()}
              </div>
              <span className={`pill ${ (targetUser?.pivot?.role || 'employee') === 'admin' ? 'pill-admin' : 'pill-employee'}`}>
                {(targetUser?.pivot?.role || 'employee') === 'admin' ? 'مشرف' : 'موظف'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary + Form */}
      <div className="all-jobs-section px-3 mb-5">
        {/* Edit form card */}
        <div className="edit-card mb-4">
          <h4 className="section-title mb-3">تعديل بيانات المستخدم</h4>
          <form onSubmit={handleSubmit}>
            <div className="grid-2">
              <div className="form-group-modern">
                <label className="form-label">اسم المستخدم</label>
                <div className="section-text">{targetUser?.name || '—'}</div>
              </div>
              <div className="form-group-modern">
                <label className="form-label">البريد الإلكتروني</label>
                <div className="section-text">{targetUser?.email || '—'}</div>
              </div>
              <div className="form-group-modern">
                <label className="form-label">رقم الجوال</label>
                <div className="section-text">{targetUser?.phone || '—'}</div>
              </div>
              <div className="form-group-modern">
                <label className="form-label">الدور</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="form-control-modern"
                  required
                >
                  <option value="employee">موظف</option>
                  <option value="admin">مشرف</option>
                </select>
                <small className="form-hint">المشرف لديه صلاحيات إدارة أوسع داخل شركة التوظيف</small>
              </div>
            </div>
            <div className="actions mt-4" style={{ gap: 8, display: 'flex' }}>
              <button
                type="submit"
                className="btn-primary-modern"
                disabled={saving}
              >
                {saving ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
              </button>
              <button
                type="button"
                className="btn-secondary-modern"
                onClick={() => navigate(-1)}
              >
                إلغاء
              </button>
            </div>
          </form>
        </div>

        {/* Local styles for a creative edit view */}
        <style>
          {`
            .edit-hero {
              background: linear-gradient(135deg, rgba(96,143,211,0.15) 0%, rgba(74,123,196,0.15) 100%);
              border-radius: 18px;
              padding: 14px 16px;
            }
            .edit-hero-inner {
              display: flex;
              align-items: center;
              gap: 12px;
            }
            .edit-hero-badge {
              width: 44px;
              height: 44px;
              border-radius: 12px;
              display: grid;
              place-items: center;
              background: linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%);
              color: #fff;
              font-size: 22px;
              box-shadow: 0 10px 20px rgba(76, 116, 179, 0.25);
            }
            .edit-hero-title {
              font-weight: 800;
              font-size: 1.1rem;
            }
            .edit-hero-subtitle {
              color: #475569;
              font-size: 0.9rem;
            }
            .avatar-initial {
              width: 36px;
              height: 36px;
              border-radius: 10px;
              display: grid;
              place-items: center;
              background: linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%);
              color: #fff;
              font-weight: 800;
              box-shadow: 0 6px 14px rgba(76, 116, 179, 0.25);
            }
            .avatar-initial.large {
              width: 54px;
              height: 54px;
              border-radius: 14px;
              font-size: 1.1rem;
            }
            .pill {
              padding: 4px 8px;
              border-radius: 999px;
              font-size: 0.8rem;
              border: 1px solid #e5e7eb;
              margin-inline-start: 8px;
            }
            .pill-admin {
              background: #f0f7ff;
              color: #1d4ed8;
              border-color: #bfdbfe;
            }
            .pill-employee {
              background: #f7f7f7;
              color: #111827;
            }
            .summary-card {
              background: #ffffff;
              border-radius: 16px;
              border: 1px solid #eef2f7;
              box-shadow: 0 6px 20px rgba(0,0,0,0.04);
            }
            .summary-inner {
              display: flex;
              align-items: center;
              gap: 12px;
              padding: 14px 16px;
            }
            .summary-meta {
              display: flex;
              flex-direction: column;
              gap: 6px;
            }
            .summary-name {
              font-weight: 800;
              color: #0f172a;
            }
            .summary-links {
              display: flex;
              flex-wrap: wrap;
              gap: 10px;
              align-items: center;
            }
            .edit-card {
              background: #ffffff;
              border-radius: 16px;
              padding: 18px;
              box-shadow: 0 10px 30px rgba(0,0,0,0.05);
              border: 1px solid #eef2f7;
            }
            .grid-2 {
              display: grid;
              grid-template-columns: 1fr;
              gap: 14px;
            }
            @media (min-width: 768px) {
              .grid-2 {
                grid-template-columns: 1fr 1fr;
              }
            }
            .form-group-modern {
              display: flex;
              flex-direction: column;
              gap: 6px;
            }
            .form-control-modern {
              width: 100%;
              border: 1px solid #e5e7eb;
              background: #fafafa;
              padding: 12px 14px;
              border-radius: 12px;
              outline: none;
              transition: box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
            }
            .form-control-modern:focus {
              border-color: #4a7bc4;
              background: #fff;
              box-shadow: 0 0 0 4px rgba(74, 123, 196, 0.15);
            }
            .form-hint {
              color: #6b7280;
              font-size: 0.8rem;
            }
            .user-cell {
              display: flex;
              align-items: center;
              gap: 10px;
            }
            .section-title {
              font-weight: 800;
              font-size: 1.1rem;
              color: #0f172a;
              margin-bottom: 1rem;
            }
            .section-text {
              color: #334155;
              font-size: 0.95rem;
            }
            .btn-primary-modern {
              background: linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%);
              color: #fff;
              border: none;
              border-radius: 12px;
              padding: 12px 20px;
              font-weight: 700;
              cursor: pointer;
              box-shadow: 0 10px 20px rgba(76, 116, 179, 0.25);
            }
            .btn-primary-modern:disabled {
              opacity: 0.75;
              cursor: not-allowed;
              box-shadow: none;
            }
            .btn-secondary-modern {
              background: #ffffff;
              color: #374151;
              border: 1px solid #e5e7eb;
              border-radius: 12px;
              padding: 12px 16px;
              font-weight: 700;
              cursor: pointer;
            }
          `}
        </style>
      </div>

      <RecruitmentCompanyBottomNavigation />
    </div>
  );
};

export default UserEdit;

